import {getAxios, Pageable, PaginationSettings} from '@deviceinsight/ng-ui-api-client';
import {AxiosResponse} from 'axios';

export type DataProcessingDefinitionDatapointDataType = 'BOOLEAN' | 'DOUBLE' | 'LONG' | 'TEXT';

export type DataProcessingDefinitionProcessingDefType = 'expressionPD';

export type DataProcessingDefinitionProcessingPeriod = 'INSTANT' | 'MIN1' | 'MIN15' | 'MIN30' | 'HOUR';

export type DataProcessingDefinitionDatapoint = {
	datapointKey: string;
	variableName: string;
	dataType: DataProcessingDefinitionDatapointDataType;
	requiresValueInCurrentPeriod: boolean;
};

export type DataProcessingDefinitionOutput = {
	outputKey: string;
	variableName: string;
};

export type DataProcessingDefinitionBody = {
	id?: string;
	version?: number;
	processingDefType: string;
	kpiName: string;
	processingPeriod: DataProcessingDefinitionProcessingPeriod;
	resetTo: string;
	ensureBoundaryValues: boolean;
	expression: string;
	input: Array<DataProcessingDefinitionDatapoint>;
	output: Array<DataProcessingDefinitionOutput>;
	publishAsDatapointValues?: boolean;
};

export type DataProcessingDefinition = {
	id: number;
	directlyAssigned: boolean;
	etag?: string | null | undefined;
	definition: DataProcessingDefinitionBody;
};

export type DataProcessingDefinitionAssignment = {
	id: number;
	kpiName: string;
};

export type AssetAssignment = {
	assetId: number;
	assetUrn: string;
	assetName: string;
};

export type DataProcessingDefinitionAssetAssignment = DataProcessingDefinitionAssignment & {
	assignedToAssets: Array<AssetAssignment>;
};

export type AssetGroupAssignment = {
	assetGroupId: number;
	assetGroupName: string;
	assetGroupDisplayName: string;
};

export type DataProcessingDefinitionAssetGroupAssignment = DataProcessingDefinitionAssignment & {
	assignedToAssetGroups: Array<AssetGroupAssignment>;
};

export type AccountGroupAssignment = {
	accountGroupId: number;
	accountGroupName: string;
	accountGroupDisplayName: string;
};

export type DataProcessingDefinitionAccountGroupAssignment = DataProcessingDefinitionAssignment & {
	accessesForAccountGroups: Array<AccountGroupAssignment>;
};

export type DataProcessingDefinitionValidation = {
	id: number;
	name: string;
	compatibleDatapoints: Array<string>;
	missingDatapoints: Array<string>;
};

export type DataProcessingDefinitionAccessList = {
	accountGroupId: number;
};

type EvaluationsParamsInput = {
	variableName: string;
	datapointKey: string;
	dataType: string;
};

export type EvaluationsParams = {
	expression: string;
	period: string;
	from: string;
	to: string;
	inputs: Array<EvaluationsParamsInput>;
};

export type DataProcessingDefinitionEvaluationSeries = {
	series: Array<{
		variable: string;
		type: string;
		data: Array<[string, number]>;
	}>;
};

export type DataProcessingDefinitionsListFilterSettings = {
	kpiName?: string;
	publishAsDatapointValues?: string;
};

const BASE_URL = '/rafi/v1/dataProcessingDefinitions';

export const listDataProcessingDefinitions = (
	filterSettings: DataProcessingDefinitionsListFilterSettings,
	paginationSettings: PaginationSettings
): Promise<Pageable<DataProcessingDefinition>> => {
	const settings = paginationSettings || {
		page: 0,
		size: 10,
		sort: ''
	};
	const {page, size, sort} = settings;
	const params = {
		...filterSettings,
		page,
		size,
		sort: (sort && `${sort.property},${sort.direction}`) || '',
		ignoreCase: true
	};
	return getAxios()
		.get(`${BASE_URL}`, {
			params
		})
		.then(extractData);
};

export const listDataProcessingDefinitionAssetAssignments = (
	dataProcessingDefinitionIds: Array<number>
): Promise<Array<DataProcessingDefinitionAssetAssignment>> => {
	return getAxios()
		.get(`${BASE_URL}/assignments/assets`, {
			params: {
				id: dataProcessingDefinitionIds
			}
		})
		.then(extractData);
};

export const listDataProcessingDefinitionAssetGroupAssignments = (
	dataProcessingDefinitionIds: Array<number>
): Promise<Array<DataProcessingDefinitionAssetGroupAssignment>> => {
	return getAxios()
		.get(`${BASE_URL}/assignments/assetGroups`, {
			params: {
				id: dataProcessingDefinitionIds
			}
		})
		.then(extractData);
};

export const listDataProcessingDefinitionAccountGroupAssignments = (
	dataProcessingDefinitionIds: Array<number>
): Promise<Array<DataProcessingDefinitionAccountGroupAssignment>> => {
	return getAxios()
		.get(`${BASE_URL}/accesses`, {
			params: {
				id: dataProcessingDefinitionIds
			}
		})
		.then(extractData);
};

export const getDataProcessingDefinitionById = (definitionId: number): Promise<DataProcessingDefinition> => {
	return getAxios()
		.get(`${BASE_URL}/${definitionId}`)
		.then((response) => {
			const data = extractData(response);
			const etag = response.headers?.etag || '';
			return {...data, etag};
		});
};

export const fetchDataProcessingDefinitionsAssignedToAsset = (
	assetId: number
): Promise<Array<DataProcessingDefinition>> => {
	return getAxios().get(`${BASE_URL}/assets?assetId=${assetId}`).then(extractData);
};

export const fetchDataProcessingDefinitionsAssignedToAssetGroup = (
	assetGroupId: number
): Promise<Array<DataProcessingDefinition>> => {
	return getAxios().get(`${BASE_URL}/assetGroups?assetGroupId=${assetGroupId}`).then(extractData);
};

export const createDataProcessingDefinition = (dataProcessingDefinition: {
	definition: DataProcessingDefinitionBody;
	assetIdsWithAccess?: number[];
	accountGroupIdsWithAccess?: number[];
}): Promise<DataProcessingDefinition> => {
	return getAxios().post(`${BASE_URL}`, dataProcessingDefinition).then(extractData);
};

export const updateDataProcessingDefinition = (data: DataProcessingDefinition): Promise<DataProcessingDefinition> => {
	const {etag, ...dataProcessingDefinition} = data;
	return getAxios()
		.put(`${BASE_URL}/${dataProcessingDefinition.id}`, dataProcessingDefinition, {
			headers: etag
				? {
						'If-Match': etag
				  }
				: {}
		})
		.then(extractData);
};

export const assignDataProcessingDefinitionsToAsset = (
	assetId: number,
	dataProcessingDefinitionIds: Array<number>
): Promise<Array<number>> => {
	return getAxios().put(`${BASE_URL}/assignments/assets/${assetId}`, dataProcessingDefinitionIds).then(extractData);
};

export const assignDataProcessingDefinitionsToAssetGroup = (
	assetGroupId: number,
	dataProcessingDefinitionIds: Array<number>
): Promise<Array<number>> => {
	return getAxios()
		.put(`${BASE_URL}/assignments/assetGroups/${assetGroupId}`, dataProcessingDefinitionIds)
		.then(extractData);
};

export const deleteDataProcessingDefinition = (dataProcessingDefinitionId: number): Promise<any> => {
	return getAxios().delete(`${BASE_URL}/${dataProcessingDefinitionId}`);
};

export const validateDataProcessingDefinitionForAssetGroup = (
	dataProcessingDefinition: DataProcessingDefinition,
	assetGroupId: number,
	paginationSettings: PaginationSettings
): Promise<Pageable<DataProcessingDefinitionValidation>> => {
	const {definition} = dataProcessingDefinition;
	const settings = paginationSettings || {
		page: 0,
		size: 10,
		sort: ''
	};
	const {page, size, sort} = settings;
	return getAxios()
		.post(
			`${BASE_URL}/validate/assetGroups/${assetGroupId}`,
			{
				definition
			},
			{
				params: {
					page,
					size,
					sort: sort ? `${sort.property},${sort.direction}` : '',
					ignoreCase: true
				}
			}
		)
		.then(extractData);
};

export const addAccessToDataProcessingDefinition = (definitionId: number, accountGroupId: number): Promise<any> => {
	return getAxios().put(`${BASE_URL}/${definitionId}/access/${accountGroupId}`);
};

export const removeAccessToDataProcessingDefinition = (definitionId: number, accountGroupId: number): Promise<any> => {
	return getAxios().delete(`${BASE_URL}/${definitionId}/access/${accountGroupId}`);
};

export const getDataProcessingDefinitionEvaluations = (
	urn: string,
	params: EvaluationsParams
): Promise<DataProcessingDefinitionEvaluationSeries> =>
	getAxios()
		.post(`${BASE_URL}/evaluation/${encodeURIComponent(urn)}`, params)
		.then(extractData);

const extractData = <T>(response: AxiosResponse<T>): T => {
	if (!response || response.data === undefined) {
		throw Error('response object has no "data" entry');
	}

	return response.data;
};
